import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { setUnauthorizedHandler } from "@/lib/api";

const AuthCtx = createContext(null);

export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  const logoutLocal = useCallback((msg) => {
    setUser(null);
    setUnauthorizedHandler(null);
    if (msg) toast.error(msg);
    navRef.current("/login", { replace: true });
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        // Only arm the 401 handler after we know we have a session.
        // Public anonymous visits would otherwise 401 and redirect.
        setUnauthorizedHandler((detail) => logoutLocal(detail));
      })
      .catch(() => {
        // Not logged in — silent. Handler stays disarmed.
      })
      .finally(() => setLoading(false));
  }, [logoutLocal]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    setUnauthorizedHandler((detail) => logoutLocal(detail));
    return data.user;
  }, [logoutLocal]);

  const register = useCallback(async (email, password, full_name) => {
    const { data } = await api.post("/auth/register", { email, password, full_name });
    setUser(data.user);
    setUnauthorizedHandler((detail) => logoutLocal(detail));
    return data.user;
  }, [logoutLocal]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_err) {
      // Cookie is cleared by the browser/server regardless; ignore.
    }
    logoutLocal();
  }, [logoutLocal]);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};
